import { useState } from 'react'
import { Formik, Form } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Icon } from '@mui/material'
import { eachDayOfInterval, format, parse } from 'date-fns'

import { usePostNewLeaveRequest } from 'services/api/modules/leave/leaveRequest'
import { usePostValidateRequestDate } from 'services/api/modules/leave/calendar'
import { useHrMyAccount } from 'services/api/hr-api/hrHooks'

import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import SelectLeaveView from './selectLeaveView'
import FillLeaveView from './fillLeaveView'
import ConfirmLeaveView from './confirmLeaveView'
import getLeaveRequestErrorMessage from 'module/leave/util/getLeaveRequestErrorMessage'

const CreateRequestModal = ({ selectedId = {}, modalOpen = true, setModalOpen }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [pageState, setPageState] = useState('SELECT_LEAVE')
  const [selectedLeave, setSelectedLeave] = useState(selectedId)
  const [schema, setSchema] = useState()
  const [requestDateError, setRequestDateError] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const mutation = usePostNewLeaveRequest()
  const mutationValidateDate = usePostValidateRequestDate()
  const { data: myAccountData } = useHrMyAccount()
  // const [modalOpen, setModalOpen] = useState(true)

  const initialValues = {
    selectedLeave: JSON.stringify(selectedLeave),
    shiftLeave: 'Full Day',
    startDate: '',
    endDate: '',
    duration: 0,
    reason: '',
    file: '',
  }

  const getValidationSchema = (schema) => {
    setSchema(schema)
  }

  const submitForm = async (values) => {
    if (pageState === 'FILL_DETAILS') {
      mutationValidateDate.mutate(
        {
          policyId: selectedLeave.leave_policy.id,
          datesArr: eachDayOfInterval({
            start: parse(values.startDate, `dd/MM/yyyy`, new Date()),
            end: parse(values.endDate, `dd/MM/yyyy`, new Date()),
          }).map((date) => format(date, 'yyyy-MM-dd')),
        },
        {
          onSuccess: () => {
            setRequestDateError(false)
            setSubmitError('')
            setIsErrorModalOpen(false)
            setPageState('CONFIRM_DETAILS')
          },
          onError: (errors) => {
            setRequestDateError(errors.data.message)
          },
        }
      )
    }

    if (pageState === 'CONFIRM_DETAILS') {
      const dateArr = eachDayOfInterval({
        start: parse(values.startDate, 'dd/MM/yyyy', new Date()),
        end: parse(values.endDate, 'dd/MM/yyyy', new Date()),
      })
      const data = {
        leave_policy_id: selectedLeave.leave_policy_id,
        reason: values.reason ? values.reason : null,
        attachment: values.file.name,
        dates: dateArr.map((date) => ({
          date: format(date, `yyyy-MM-dd`),
          type: values.shiftLeave,
        })),
      }

      setSubmitError('')
      setIsErrorModalOpen(false)
      mutation.mutate(data, {
        onSuccess: async () => {
          setSubmitError('')
          setIsErrorModalOpen(false)
          setPageState('SUCCESSFUL_REQUEST')
        },
        onError: (error) => {
          setSubmitError(
            getLeaveRequestErrorMessage({
              errorMessage: error?.data?.message,
              selectedLeave,
              myAccountData,
            })
          )
          setIsErrorModalOpen(true)
        },
      })
    }
  }

  const handleCloseModal = (event, reason) => {
    if (reason === 'backdropClick') return

    if (location.pathname === '/app/homepage') {
      return setModalOpen(false)
    } else {
      navigate(-1)
    }
  }

  const handleErrorClose = () => {
    setIsErrorModalOpen(false)
  }

  if (pageState === 'SUCCESSFUL_REQUEST') {
    return (
      <AdxInfoModal
        open={modalOpen}
        muiIcon="check_circle"
        iconColor="alternate.main"
        title="Leave request submitted"
        subtitle={`Your ${selectedLeave.leave_policy.name} request has been successfully submitted.`}
        footerButtons
        firstBtnLabel={'Return to dashboard'}
        firstBtnHandle={
          location.pathname === '/app/homepage'
            ? () => handleCloseModal()
            : () => navigate('/app/leave/request')
        }
        secondBtnLabel={'View all request'}
        secondBtnHandle={() => navigate(`/app/leave/request/all`, { backgroundLocation: null })}
      ></AdxInfoModal>
    )
  }

  return (
    <AdxFormModal
      open={modalOpen}
      onClose={handleCloseModal}
      handleClose={handleCloseModal}
      disableEscapeKeyDown
      disableAutoFocus
      title={'Request for Leave'}
      pageState={pageState}
      childPadding={false}
      icon={<Icon sx={{ color: 'alternate.main' }}>calendar_today</Icon>}
      sxModal={{
        width: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 24px)', md: '600px' },
        minWidth: { xs: 'calc(100vw - 24px)', md: '600px' },
        maxWidth: { xs: 'calc(100vw - 24px)', sm: 'calc(100vw - 24px)', md: '600px' },
        maxHeight: { xs: 'calc(100dvh - 24px)', sm: '90vh' },
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '10px',
      }}
    >
      <Box>
        <Formik
          initialValues={initialValues}
          validateOnChange={true}
          validationSchema={schema}
          onSubmit={(values) => {
            submitForm(values)
          }}
        >
          {(formikProps) => (
            <Form>
              {pageState === 'SELECT_LEAVE' && (
                <SelectLeaveView
                  setPageState={setPageState}
                  setSelected={setSelectedLeave}
                  formikProps={formikProps}
                />
              )}
              {pageState === 'FILL_DETAILS' && (
                <FillLeaveView
                  setPageState={setPageState}
                  selectedLeave={selectedLeave}
                  setSelected={setSelectedLeave}
                  formikProps={formikProps}
                  getValidationSchema={getValidationSchema}
                  requestDateError={requestDateError}
                />
              )}
              {pageState === 'CONFIRM_DETAILS' && (
                <ConfirmLeaveView
                  setPageState={setPageState}
                  selectedLeave={selectedLeave}
                  formikProps={formikProps}
                  isSubmitting={mutation.isLoading}
                  setSubmitError={setSubmitError}
                />
              )}
            </Form>
          )}
        </Formik>
      </Box>
      <AdxInfoModal
        variant="error"
        open={isErrorModalOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleErrorClose()
          }
        }}
        handleClose={handleErrorClose}
        muiIcon="error"
        iconColor="error.main"
        title="Unable to submit leave request"
        subtitle={submitError}
        footerButtons
        firstBtnLabel="Cancel"
        firstBtnHandle={handleErrorClose}
      />
    </AdxFormModal>
  )
}

export default CreateRequestModal
